import { Component, OnInit, inject, ChangeDetectionStrategy, DestroyRef, signal, computed } from '@angular/core';
import { Skeleton } from '@openng/optimus-ui/skeleton';
import { Router } from '@angular/router';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import { DashboardManagementService, ManagedDashboard } from '../../features/dashboard/services/dashboard.service';
import { LoginService } from '../../core/services/login.service';
import { NavigationService, MenuItem as AppMenuItem, Company } from '../../core/services/navigation.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { LogService } from '../../core/services/log.service';
import { SidenavMenuComponent, SidenavMenuItem } from './sidenav-menu/sidenav-menu.component';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-navigation',
    imports: [SidenavMenuComponent, Skeleton],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
      :host {
        display: block;
        height: 100%;
        min-height: 100%;
      }

      .stela-sidebar {
        /* Viewport height, not 100%: the grid cell stretches to the page, and a
           100% here beat the global sticky 100vh rule (higher specificity), so
           the whole menu scrolled away with long pages. */
        height: 100vh;
        color: var(--stela-menu-item);
        border-right: 1px solid var(--stela-menu-border);
        overflow: hidden !important;
      }

      .stela-sidebar__inner {
        display: flex;
        flex-direction: column;
        min-height: 100%;
        height: 100%;
        overflow: hidden;
      }

      /* Brand block — el lockup horizontal oficial de Claude Design
         (assets/Stela_logo_horizontal.png: corona + S + wordmark STELA +
         separador + tagline, todo en una sola imagen). En condensed se
         cambia por la marca corona+S sola, como en el prototipo Stela 2. */
      .sidebar-brand {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 18px;
        /* Same height as the topbar, as in the Stela 2 prototype (.sidebar-brand). */
        min-height: var(--stela-layout-topbar-height);
        border-bottom: 1px solid var(--stela-menu-separator);
        flex-shrink: 0;
      }
      .sidebar-brand .brand-logo {
        display: block;
        height: 32px;
        width: auto;
        max-width: 100%;
        flex-shrink: 0;
      }
      .sidebar-brand .brand-mark-collapsed {
        display: none;
        height: 28px;
        width: auto;
        flex-shrink: 0;
      }

      .sidebar-scroll-area {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
      }
      .sidebar-scroll-area::-webkit-scrollbar { width: 4px; }
      .sidebar-scroll-area::-webkit-scrollbar-track { background: transparent; }
      .sidebar-scroll-area::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
      }
      .sidebar-scroll-area::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.35);
      }

      app-sidenav-menu { display: block; }

      .sidebar-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 12px 16px;
        border-top: 1px solid var(--stela-menu-separator);
        color: var(--stela-menu-text-muted);
        font-size: var(--stela-fs-tiny);
      }
      .sidebar-foot .company-mark,
      .sidebar-foot .diaple-mark {
        max-height: 22px;
        max-width: 130px;
        width: auto;
        object-fit: contain;
        object-position: left center;
      }
      .sidebar-foot .diaple-mark {
        height: 16px;
        max-height: 16px;
        opacity: 0.7;
      }
      .sidebar-foot .foot-version {
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
      }

      /* Sidebar skeleton — while loadCompany+loadMenu are pending */
      .nav-skeleton {
        padding: 12px 0;
      }
      .nav-skeleton-eyebrow {
        height: 8px;
        margin: 16px 20px 12px;
        width: 60%;
      }
      .nav-skeleton-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 20px;
      }
      .nav-skeleton-bar {
        flex: 1 1 auto;
        height: 10px;
      }
      .nav-skeleton-row:nth-child(2n) .nav-skeleton-bar { width: 70%; }
      .nav-skeleton-row:nth-child(3n) .nav-skeleton-bar { width: 50%; }
      :host-context(html[data-sidenav-size="condensed"]) .nav-skeleton-eyebrow,
      :host-context(html[data-sidenav-size="condensed"]) .nav-skeleton-bar {
        display: none;
      }
      :host-context(html[data-sidenav-size="condensed"]) .nav-skeleton-row {
        justify-content: center;
      }

      /* Mobile close drawer button (offcanvas) */
      .close-drawer-btn {
        position: absolute;
        top: 8px;
        right: 8px;
        background: transparent;
        border: 0;
        color: var(--stela-menu-text-muted);
        font-size: 18px;
        cursor: pointer;
        display: none;
      }
      :host-context(html[data-sidenav-size="offcanvas"].sidebar-enable) .close-drawer-btn {
        display: block;
      }

      /* Condensed (collapsed) sidebar — show only the diamond mark */
      :host-context(html[data-sidenav-size="condensed"]) .sidebar-brand {
        padding: 0;
        justify-content: center;
        gap: 0;
      }
      :host-context(html[data-sidenav-size="condensed"]) .sidebar-brand .brand-logo {
        display: none;
      }
      :host-context(html[data-sidenav-size="condensed"]) .sidebar-brand .brand-mark-collapsed {
        display: block;
      }
      :host-context(html[data-sidenav-size="condensed"]) .sidebar-foot .foot-version {
        display: none;
      }
      :host-context(html[data-sidenav-size="condensed"]) .sidebar-foot {
        justify-content: center;
      }
    `],
    template: `
<nav class="stela-sidebar" role="navigation">
  <div class="stela-sidebar__inner">
    <button type="button" class="close-drawer-btn" (click)="closeDrawer()" aria-label="Close sidebar">
      <i class="pi pi-times"></i>
    </button>

    <!-- Brand — lockup horizontal oficial (expandido) + marca corona+S sola (condensed) -->
    <div class="sidebar-brand" aria-label="Stela · Protocolo y Eventos">
      <img class="brand-logo"
           src="/assets/img/Stela_logo_horizontal.png"
           alt="Stela · Protocolo y Eventos"
           loading="eager"
           decoding="sync" />
      <img class="brand-mark-collapsed"
           src="/assets/img/Logo_stela_crown_s.png"
           alt="Stela"
           loading="eager"
           decoding="sync" />
    </div>

    <!-- Scrollable area -->
    <div class="sidebar-scroll-area">
      @if (menuLoaded()) {
        <app-sidenav-menu [items]="navigationItems()"></app-sidenav-menu>
        <app-sidenav-menu [items]="systemItems()"></app-sidenav-menu>
      } @else {
        <!-- Skeleton while loadCompany + loadMenu are pending. -->
        <div class="nav-skeleton" aria-hidden="true">
          <div class="nav-skeleton-eyebrow"><p-skeleton height="8px" styleClass="bg-white/6! animate-pulse" animation="none" borderRadius="4px" /></div>
          @for (i of skeletonRows; track i) {
            <div class="nav-skeleton-row">
              <p-skeleton width="16px" height="16px" styleClass="shrink-0 bg-white/8! animate-pulse" animation="none" borderRadius="4px" />
              <span class="nav-skeleton-bar"><p-skeleton height="10px" styleClass="bg-white/6! animate-pulse" animation="none" borderRadius="4px" /></span>
            </div>
          }
          <div class="nav-skeleton-eyebrow"><p-skeleton height="8px" styleClass="bg-white/6! animate-pulse" animation="none" borderRadius="4px" /></div>
          @for (i of skeletonRows; track i) {
            <div class="nav-skeleton-row">
              <p-skeleton width="16px" height="16px" styleClass="shrink-0 bg-white/8! animate-pulse" animation="none" borderRadius="4px" />
              <span class="nav-skeleton-bar"><p-skeleton height="10px" styleClass="bg-white/6! animate-pulse" animation="none" borderRadius="4px" /></span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Foot — company logo (falls back to the Diaple mark until it loads) + app version -->
    <div class="sidebar-foot">
      @if (company?.logo) {
        <img class="company-mark" [src]="company!.logo" [alt]="company!.name" />
      } @else {
        <img class="diaple-mark" src="assets/img/logo-diaple.jpeg" alt="Diaple" />
      }
      <span class="foot-version">v{{ appVersion }}</span>
    </div>
  </div>
</nav>
`
})
export class NavigationComponent implements OnInit {
    company: Company | null = null;
    private readonly destroyRef = inject(DestroyRef);
    readonly sidebarService = inject(SidebarService);

    private readonly dashboardsService = inject(DashboardManagementService);
    private static readonly HOME_ROUTES = new Set(['/dashboard', '/dashboard/list']);

    // Home children are data (the company's dashboards), so they are injected here instead of living in the menu tables.
    // A failed load must never break the sidebar: it just leaves Inicio without children.
    private readonly homeDashboards = toSignal(
        this.dashboardsService.changes$.pipe(
            startWith(undefined),
            switchMap(() => this.dashboardsService.getDashboards().pipe(
                catchError(() => of([] as ManagedDashboard[]))
            ))
        ),
        { initialValue: [] as ManagedDashboard[] }
    );

    readonly appVersion = (environment as any).version ?? '2.4.1';

    // Signal to store app menu items from API
    private readonly appMenuItems = signal<AppMenuItem[]>([]);

    /** True once the menu API call has returned at least once (success or error). */
    readonly menuLoaded = signal(false);

    /** Placeholder row count for the skeleton. */
    readonly skeletonRows = [0, 1, 2, 3, 4, 5];

    /**
     * IDs (case-insensitive) de los grupos top-level que pertenecen a la sección "Sistema".
     * El resto va a "Navegación". Se compara contra `label` normalizado a slug.
     */
    private static readonly SYSTEM_GROUP_LABELS = new Set<string>([
        'comunicaciones',
        'avanzado',
        'configuracion',
        'sistema',
        'archivos',
        'ayuda',
    ]);

    navigationItems = computed<SidenavMenuItem[]>(() => {
        const all = this.convertToSidenavItems(this.appMenuItems());
        return all.filter(i => !this.isSystem(i.label));
    });

    systemItems = computed<SidenavMenuItem[]>(() => {
        const all = this.convertToSidenavItems(this.appMenuItems());
        return all.filter(i => this.isSystem(i.label));
    });

    private isSystem(label: string): boolean {
        const slug = (label || '').trim().toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '');
        return NavigationComponent.SYSTEM_GROUP_LABELS.has(slug);
    }

    constructor(
        public loginService: LoginService,
        public navigationService: NavigationService,
        public router: Router,
        private log: LogService
    ) {}

    ngOnInit(): void {
        const companyId = parseInt(this.loginService.get('companyId') || '0');
        if (companyId > 0) {
            this.navigationService.loadCompany(companyId).pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
                next: (company) => {
                    this.company = company;
                    this.navigationService.setCompany(company);
                    this.loadMenu(company.menu_code);
                },
                error: (err) => this.log.error('Error loading company', err)
            });
        }

        this.navigationService.menu$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(items => {
            this.appMenuItems.set(items);
            if (items && items.length > 0) {
                this.menuLoaded.set(true);
            }
        });
    }

    loadMenu(menuCode: string): void {
        this.navigationService.loadMenu(menuCode).subscribe({
            next: (items) => {
                this.navigationService.setMenu(items);
                this.menuLoaded.set(true);
            },
            error: (err) => {
                this.log.error('Error loading menu', err);
                // Surface the skeleton hiding even on error so the user sees
                // the empty state and can navigate by URL instead of a frozen sidebar.
                this.menuLoaded.set(true);
                if (err.status === 401 || err.status === 440) {
                    this.loginService.closeSession();
                }
            }
        });
    }

    closeDrawer(): void {
        this.sidebarService.closeDrawer();
    }

    /**
     * Convert API menu items to the SidenavMenuItem shape used by the custom menu.
     * Filters to top-level items (no parent), maps children recursively.
     */
    private convertToSidenavItems(items: AppMenuItem[]): SidenavMenuItem[] {
        return items
            .filter(item => item.visible)
            .filter(item => {
                const parentId = item.parent_menu_item_id ?? item.ParentMenuItem?.id ?? null;
                return parentId == null;
            })
            .map(item => this.convertSingle(item));
    }

    private convertSingle(item: AppMenuItem): SidenavMenuItem {
        const children = (item.ChildMenuItems ?? [])
            .filter(c => c.visible)
            .map(c => this.convertSingle(c));

        const isHome = !!item.route && NavigationComponent.HOME_ROUTES.has(item.route);

        return {
            id: item.id,
            label: this.getMenuName(item),
            icon: item.icon || '',
            route: item.route,
            href: item.to_href,
            visible: item.visible,
            children: isHome ? [...children, ...this.homeChildren()] : children,
            collapsed: true // start collapsed; SidenavMenuComponent will auto-expand active parents
        };
    }

    private homeChildren(): SidenavMenuItem[] {
        return [...this.homeDashboards()]
            .filter(d => !d.isTemplate)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(d => ({
                id: 1_000_000 + d.id,
                label: d.name,
                icon: '',
                route: `/dashboard/${d.id}`,
                href: null,
                visible: true,
                children: [],
                collapsed: true
            }));
    }

    private getMenuName(item: AppMenuItem): string {
        const translations = item.MenusItemsTls;
        if (translations && translations.length > 0 && translations[0].name != null) {
            return translations[0].name;
        }
        return item.name;
    }
}
